/**
 * A phone's menu from the bottom — a handle and rows, nothing else
 * (`new-request.md` Board 1 G, Board 16 D). Not `PanelSheet`: that one is a
 * panel with a title, a live count and a footer button, and a two-row choice
 * has none of the three.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'

export function BottomMenu({
  open, onClose, label, children, layer = 60,
}: {
  open: boolean
  onClose: () => void
  label: string
  children: ReactNode
  layer?: number
}) {
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); onClose() }
    }
    window.addEventListener('keydown', onKey)
    box.current?.querySelector<HTMLElement>('button')?.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return createPortal(
    <div
      style={{ zIndex: layer }}
      data-overlay-layer={layer}
      className="fixed inset-0 flex flex-col justify-end bg-scrim/30"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div
        ref={box}
        role="menu"
        aria-label={label}
        className={cn('rounded-t-sheet-wide bg-surface pb-[calc(22px+env(safe-area-inset-bottom))] pt-2',
          'shadow-[0_-10px_30px_rgba(20,20,10,.14)]')}
      >
        <div className="flex justify-center pb-2">
          <i className="block h-1 w-10 rounded-full bg-line-strong" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
